import { BookOpenIcon, CameraIcon, UserGroupIcon, ShieldCheckIcon, HeartIcon, ExclamationCircleIcon, CpuChipIcon, BuildingOfficeIcon, UserIcon, CogIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useDocsCategories } from '@/hooks/useWordPressDocs';
import { useNavigate } from 'react-router-dom';
import { useSupportContext } from '@/contexts/SupportContext';

// Icon mapping for actual BetterDocs categories
const categoryIcons: Record<string, any> = {
  // Core categories
  'get-started': BookOpenIcon,
  'daily-check-in': HeartIcon,
  'thrivescore-wellness': CpuChipIcon,
  'community-resources': UserGroupIcon,
  'thrivevision-ai': CameraIcon,
  'family-features': UserGroupIcon,
  'thrivecircle-connections': UserGroupIcon,
  'notifications-reminders': ExclamationCircleIcon,
  'caregiver-tools': HeartIcon,
  'integrations-devices': CogIcon,
  'sponsors-partners': BuildingOfficeIcon,
  'billing-account': CogIcon,
  'plans-upgrades': CogIcon,
  'troubleshooting-support': ExclamationCircleIcon,
  'tech-support': CogIcon,
  'privacy-security': ShieldCheckIcon,
  'families': UserGroupIcon,
  'caregivers': HeartIcon,
  'sponsors': BuildingOfficeIcon,
};

const ResourceCategories = () => {
  const navigate = useNavigate();
  const { selectedAudience } = useSupportContext();
  const { data: categories, isLoading: categoriesLoading } = useDocsCategories();


  if (categoriesLoading) {
    return (
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Loading <span className="text-primary">Resource Library</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader className="space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-6 bg-muted rounded w-full"></div>
                  <div className="h-4 bg-muted rounded w-5/6"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, j) => (
                      <div key={j} className="h-3 bg-muted rounded w-full"></div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const handleCategoryClick = (category: any) => {
    navigate(`/support/category/${category.slug}`);
  };

  // Filter categories based on selected audience
  const filteredCategories = categories?.filter(category => {
    if (!selectedAudience) return true; // Show all if no audience selected
    
    // Map audiences to actual category slugs from BetterDocs
    const audienceToSlugs = {
      'older-adults': ['daily-check-in', 'thrivescore-wellness', 'community-resources', 'thrivevision-ai'],
      'families': ['families', 'family-features', 'thrivecircle-connections', 'notifications-reminders'],
      'caregivers': ['caregivers', 'caregiver-tools', 'integrations-devices'],
      'sponsors': ['sponsors', 'sponsors-partners']
    };
    
    // Always show general categories for all audiences
    const alwaysShowSlugs = ['get-started', 'tech-support', 'troubleshooting-support', 'privacy-security', 'plans-upgrades', 'billing-account'];
    if (alwaysShowSlugs.includes(category.slug)) return true;
    
    // Check if category slug matches the selected audience
    const audienceSlugs = audienceToSlugs[selectedAudience as keyof typeof audienceToSlugs] || [];
    return audienceSlugs.includes(category.slug);
  });

  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Your Complete <span className="text-primary">Resource Library</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Organized by topic, designed for clarity, and created with your success in mind. 
            Every resource connects back to why it matters for your independence and wellbeing.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {filteredCategories?.map((category) => {
            const IconComponent = categoryIcons[category.slug] || BookOpenIcon;
            
            return (
              <Card 
                key={category.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => handleCategoryClick(category)}
              >
                <CardHeader>
                  <div className="flex items-center mb-2">
                    <IconComponent className="h-6 w-6 text-primary mr-3" />
                    <span className="text-sm font-medium text-primary">
                      {category.count} Resources
                    </span>
                  </div>
                  <CardTitle className="group-hover:text-primary transition-colors">
                    {category.name}
                  </CardTitle>
                  <CardDescription>
                    {category.description || `Comprehensive guides and resources for ${category.name.toLowerCase()}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mt-4 pt-4 border-t">
                    <button 
                      onClick={() => handleCategoryClick(category)}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      View Resources →
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ResourceCategories;